<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import { computed } from 'vue';

import ActionButton from '~/components/shared/ActionButton.vue';
import Pagination from '~/components/Pagination';

import type { IncentiveListItem } from '../types';

import { formatIncentiveDate, formatIncentiveNumber } from '../utils';
import IncentiveStatusBadge from './IncentiveStatusBadge.vue';

const props = defineProps<{
  currentPage: number;
  hasNext: boolean;
  isLoading: boolean;
  pageSize: number;
  rows: IncentiveListItem[];
  totalCount: number;
}>();

const emit = defineEmits<{
  'page-change': [page: number];
  'page-size-change': [pageSize: number];
  view: [incentive: IncentiveListItem];
}>();

const startRow = computed(() => (props.currentPage - 1) * props.pageSize + 1);
const endRow = computed(() => Math.min(props.currentPage * props.pageSize, props.totalCount));
</script>

<template>
  <div class="col-12">
    <div class="inc-card">
      <div class="inc-table-wrap">
        <table class="inc-table">
          <thead>
            <tr>
              <th>Incentivo</th>
              <th>Descrição</th>
              <th class="text-center">Campanhas</th>
              <th>Criado em</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>

          <template v-if="isLoading">
            <tbody>
              <tr v-for="i in 8" :key="i">
                <td v-for="j in 6" :key="j">
                  <div class="placeholder-glow">
                    <span class="placeholder col-12 placeholder-row" />
                  </div>
                </td>
              </tr>
            </tbody>
          </template>

          <tbody v-else>
            <tr v-if="rows.length === 0">
              <td colspan="6" class="text-center py-4" style="color: #6b7280;">
                Nenhum incentivo encontrado
              </td>
            </tr>

            <tr v-for="incentive in rows" v-else :key="incentive.id">
              <td>
                <RouterLink
                  class="cell-link"
                  :to="{ path: '/incentive-detail', query: { id: incentive.id } }"
                  @click="emit('view', incentive)"
                >
                  {{ incentive.name }}
                </RouterLink>
                <div class="cell-muted">criado por {{ incentive.createdBy }}</div>
              </td>

              <td class="cell-strong">{{ incentive.descriptionPtbr }}</td>

              <td class="text-center">{{ formatIncentiveNumber(incentive.campaignsCount) }}</td>

              <td>{{ formatIncentiveDate(incentive.createdAt) }}</td>

              <td>
                <IncentiveStatusBadge :status="incentive.status" />
              </td>

              <td>
                <ActionButton type="details" @click="emit('view', incentive)" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoading" class="inc-pagination">
        <span>
          <template v-if="totalCount > 0">
            Mostrando {{ startRow }}–{{ endRow }} de {{ totalCount }} incentivos
          </template>
          <template v-else>
            Nenhum incentivo encontrado
          </template>
        </span>

        <Pagination
          v-if="totalCount > 0"
          class="inc-pagination-controls"
          :current-page="currentPage"
          :has-next="hasNext"
          :page-size="pageSize"
          @page-change="emit('page-change', $event)"
          @page-size-change="emit('page-size-change', $event)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.inc-card {
  background: #fff;
  border: 1px solid #e4e6eb;
  border-radius: 12px;
  overflow: hidden;
}

.inc-table-wrap {
  overflow-x: auto;
}

.inc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead th {
    background: #fafafb;
    color: #6b7280;
    text-align: left;
    padding: 12px 16px;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid #e4e6eb;
    white-space: nowrap;

    &.text-center {
      text-align: center;
    }
  }

  tbody td {
    padding: 14px 16px;
    border-bottom: 1px solid #e4e6eb;
    vertical-align: middle;
    color: #222222;

    &.text-center {
      text-align: center;
    }
  }

  tbody tr:hover td {
    background: #fafbfc;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.cell-link {
  color: #00b569;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.cell-muted {
  color: #6b7280;
  font-size: 12px;
  margin-top: 2px;
}

.cell-strong {
  font-weight: 600;
  color: #222222;
}

.inc-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 20px;
  font-size: 12px;
  color: #6b7280;
  border-top: 1px solid #e4e6eb;
  flex-wrap: wrap;
}

.inc-pagination-controls {
  :deep(> .d-flex) {
    margin: 0 !important;
    padding-right: 0 !important;
    width: auto !important;
  }
}

.placeholder-row {
  display: inline-block;
  height: 16px;
  border-radius: 3px;
}
</style>
